import {
  EdgesGeometry,
  LineDashedMaterial,
  LineSegments,
  MeshBasicMaterial,
  Vector2Tuple,
} from 'three';

import { NavMesh as RecastNavMesh } from '@recast-navigation/core';
import { NavMeshHelper } from '@recast-navigation/three';

const simplifyOutline = (points: Vector2Tuple[]): Vector2Tuple[] => {
  if (points.length < 3) return points;

  const result: Vector2Tuple[] = [points[0]];
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];

    const angle1 = Math.atan2(curr[1] - prev[1], curr[0] - prev[0]);
    const angle2 = Math.atan2(next[1] - curr[1], next[0] - curr[0]);
    let angle = Math.abs(angle2 - angle1) * (180 / Math.PI);

    if (angle > 0 && (angle < 170 || angle > 190)) {
      result.push(curr);
    }
  }
  result.push(points[points.length - 1]);
  return result;
};

export function getNavMeshOutline(navMesh: RecastNavMesh): Vector2Tuple[][] {
  const color = 0x00ff00;
  const helper = new NavMeshHelper({
    navMesh: navMesh,
    navMeshMaterial: new MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.2,
    }),
  });

  const lineSegments = new LineSegments(
    new EdgesGeometry(helper.geometry),
    new LineDashedMaterial({
      color,
      vertexColors: true,
      linewidth: 4,
      scale: 1,
      dashSize: 1,
      gapSize: 1,
    })
  );
  lineSegments.computeLineDistances();

  const positions = lineSegments.geometry.attributes.position.array;

  // Convert positions to array of point pairs
  const segments: Vector2Tuple[][] = [];
  for (let i = 0; i < positions.length; i += 6) {
    segments.push([
      [positions[i], positions[i + 2]],
      [positions[i + 3], positions[i + 5]],
    ]);
  }

  // Arrange segments in continuous order
  const orderedPoints: Vector2Tuple[] = [];
  if (segments.length > 0) {
    orderedPoints.push(...segments[0]);
    segments.splice(0, 1);

    while (segments.length > 0) {
      const lastPoint = orderedPoints[orderedPoints.length - 1];
      const nextSegmentIndex = segments.findIndex(
        (segment) =>
          (segment[0][0] === lastPoint[0] && segment[0][1] === lastPoint[1]) ||
          (segment[1][0] === lastPoint[0] && segment[1][1] === lastPoint[1])
      );

      if (nextSegmentIndex === -1) break;

      const nextSegment = segments[nextSegmentIndex];
      const nextPoint =
        nextSegment[0][0] === lastPoint[0] && nextSegment[0][1] === lastPoint[1]
          ? nextSegment[1]
          : nextSegment[0];

      orderedPoints.push(nextPoint);
      segments.splice(nextSegmentIndex, 1);
    }
  }

  return [simplifyOutline(orderedPoints)];
}
